import type {
  SWIncomingMessage,
  RegisterAppResponse,
  ShouldUpdateAppResponse,
  AppFiles,
} from "./types";
import { SW_MESSAGE_TYPES } from "./types";

/**
 * Send a message to the service worker and wait for response
 * Uses MessageChannel for bidirectional communication
 *
 * @param message - The message to send to the service worker
 * @param timeout - Timeout in milliseconds (default: 5000)
 * @param messageChannel - MessageChannel instance for dependency injection (default: new MessageChannel())
 * @returns Promise that resolves with the response from service worker
 * @throws Error if service worker is not available or request times out
 */
export async function sendMessageToServiceWorker<T>(
  message: SWIncomingMessage,
  timeout: number = 5000,
  messageChannel: MessageChannel = new MessageChannel(),
): Promise<T> {
  // Check if service worker is available
  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
    throw new Error(
      "Service worker is not available or not controlling this page",
    );
  }

  // Store reference to controller to satisfy TypeScript null checking
  const controller = navigator.serviceWorker.controller;

  return new Promise<T>((resolve, reject) => {
    // Set up timeout
    const timeoutId = setTimeout(() => {
      messageChannel.port1.close();
      reject(new Error(`Service worker request timed out after ${timeout}ms`));
    }, timeout);

    // Listen for response on port1
    messageChannel.port1.onmessage = (event) => {
      clearTimeout(timeoutId);
      messageChannel.port1.close();
      resolve(event.data as T);
    };

    // Send message to service worker with port2
    try {
      controller.postMessage(message, [messageChannel.port2]);
    } catch (error) {
      clearTimeout(timeoutId);
      messageChannel.port1.close();
      reject(error);
    }
  });
}

/**
 * Check if an app needs to be updated in the service worker cache
 *
 * @param baseUrl - The base URL of the Cognite API (e.g., https://api.cognitedata.com)
 * @param project - The Cognite project name
 * @param appId - The app identifier
 * @param lastUpdatedTime - The timestamp of the latest version (Unix timestamp in ms)
 * @returns Promise that resolves with update check response
 */
export async function shouldUpdateApp(
  baseUrl: string,
  project: string,
  appId: string,
  lastUpdatedTime: number,
): Promise<ShouldUpdateAppResponse> {
  const message: SWIncomingMessage = {
    type: SW_MESSAGE_TYPES.SHOULD_UPDATE_APP,
    payload: {
      baseUrl,
      project,
      appId,
      lastUpdatedTime,
    },
  };

  return sendMessageToServiceWorker<ShouldUpdateAppResponse>(message);
}

/**
 * Register/update an app in the service worker cache
 *
 * @param baseUrl - The base URL of the Cognite API (e.g., https://api.cognitedata.com)
 * @param project - The Cognite project name
 * @param appId - The app identifier
 * @param lastUpdatedTime - The timestamp of this version (Unix timestamp in ms)
 * @param files - The app files to cache
 * @returns Promise that resolves with registration response
 */
export async function registerApp(
  baseUrl: string,
  project: string,
  appId: string,
  lastUpdatedTime: number,
  files: AppFiles,
): Promise<RegisterAppResponse> {
  const message: SWIncomingMessage = {
    type: SW_MESSAGE_TYPES.REGISTER_APP,
    payload: {
      baseUrl,
      project,
      appId,
      lastUpdatedTime,
      files,
    },
  };

  return sendMessageToServiceWorker<RegisterAppResponse>(message);
}

/**
 * Set debug mode in the service worker
 *
 * @param enabled - Whether to enable debug logging
 */
export function setDebugMode(enabled: boolean): void {
  const message: SWIncomingMessage = {
    type: SW_MESSAGE_TYPES.SET_DEBUG_MODE,
    payload: {
      enabled,
    },
  };

  // Fire and forget - no need to wait for response
  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
}
